import { useState } from 'react';
import { orgs, projects as projectsApi, ApiClientError, type ProjectInfo } from '../lib/api';
import { useResource } from '../hooks/useResource';
import { useProject } from '../hooks/useProject';
import { formatDate } from '../lib/format';
import { ROLE_LABELS, can } from '../lib/roles';
import { Alert, Badge, Card, Empty, Field, Modal, Spinner } from '../components/ui';

/**
 * Projects across every organization the user belongs to. Each project is an
 * isolated rail — its own wallet, fiat float, keys, webhooks and orders — so
 * switching here is switching which money you are looking at.
 *
 * Create/rename is owner/admin only (per organization); the buttons hide for
 * everyone else and the server enforces the same matrix.
 */
export function Projects() {
  const { project: active, projects, switchProject, reloadProjects } = useProject();
  const organizations = useResource(() => orgs.list(), []);

  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createOrgId, setCreateOrgId] = useState('');
  const [renaming, setRenaming] = useState<ProjectInfo | null>(null);
  const [renameTo, setRenameTo] = useState('');
  const [busy, setBusy] = useState(false);

  // Orgs where this user can create projects; drives whether the button shows
  // and which orgs the create form offers.
  const manageable = (organizations.data ?? []).filter((org) =>
    can(org.role, 'manage_projects')
  );

  function failed(caught: unknown, fallback: string) {
    setError(caught instanceof ApiClientError ? caught.message : fallback);
  }

  function openCreate() {
    setCreateName('');
    setCreateOrgId(manageable[0]?.id ?? '');
    setCreating(true);
  }

  async function create() {
    setError(null);
    setBusy(true);
    try {
      const created = await projectsApi.create(createOrgId, createName.trim());
      setCreating(false);
      await reloadProjects();
      // Land the user in the project they just made — that's why they made it.
      switchProject(created.id);
    } catch (caught) {
      failed(caught, 'Could not create the project');
    } finally {
      setBusy(false);
    }
  }

  async function rename() {
    if (!renaming) return;
    setError(null);
    setBusy(true);
    try {
      await projectsApi.rename(renaming.id, renameTo.trim());
      setRenaming(null);
      await reloadProjects();
    } catch (caught) {
      failed(caught, 'Could not rename the project');
    } finally {
      setBusy(false);
    }
  }

  if (organizations.loading && !organizations.data) {
    return <Spinner label="Loading your projects…" />;
  }

  // Group by organization so multi-org users see clear boundaries.
  const byOrg = new Map<string, ProjectInfo[]>();
  for (const project of projects) {
    const group = byOrg.get(project.organization_id) ?? [];
    group.push(project);
    byOrg.set(project.organization_id, group);
  }

  return (
    <>
      <div className="page-header">
        <h1>Projects</h1>
        <p>
          Each project is a fully isolated rail: its own wallet, fiat float, API keys, webhooks
          and orders. Switch projects to switch which rail you are operating.
        </p>
      </div>

      {error && <Alert>{error}</Alert>}

      {[...byOrg.entries()].map(([orgId, group]) => {
        const orgName = group[0]?.organization_name ?? '';
        const orgRole = (organizations.data ?? []).find((org) => org.id === orgId)?.role;
        const manager = can(orgRole, 'manage_projects');

        return (
          <Card
            key={orgId}
            title={orgName}
            actions={
              manager && (
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => {
                    openCreate();
                    setCreateOrgId(orgId);
                  }}
                >
                  New project
                </button>
              )
            }
          >
            <table className="table">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Your role</th>
                  <th>Created</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {group.map((project) => (
                  <tr key={project.id}>
                    <td>
                      <strong>{project.name}</strong>{' '}
                      {project.id === active?.id && <Badge tone="success">Active</Badge>}
                    </td>
                    <td>{ROLE_LABELS[project.role]}</td>
                    <td>{formatDate(project.created_at)}</td>
                    <td style={{ textAlign: 'right' }}>
                      {project.id !== active?.id && (
                        <button
                          className="btn btn-sm"
                          onClick={() => switchProject(project.id)}
                        >
                          Switch to
                        </button>
                      )}{' '}
                      {manager && (
                        <button
                          className="btn btn-sm"
                          onClick={() => {
                            setRenaming(project);
                            setRenameTo(project.name);
                          }}
                        >
                          Rename
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        );
      })}

      {projects.length === 0 && (
        <Card title="Projects">
          <Empty>You are not a member of any project yet.</Empty>
        </Card>
      )}

      {creating && (
        <Modal title="New project" onClose={() => setCreating(false)}>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void create();
            }}
          >
            {manageable.length > 1 && (
              <Field label="Organization">
                <select
                  value={createOrgId}
                  onChange={(event) => setCreateOrgId(event.target.value)}
                >
                  {manageable.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </select>
              </Field>
            )}
            <Field
              label="Project name"
              hint="A new project starts empty — its wallet and fiat float are created when you first use them."
            >
              <input
                value={createName}
                onChange={(event) => setCreateName(event.target.value)}
                placeholder="e.g. Production, Staging, NG Corridor"
                required
                minLength={2}
                autoFocus
              />
            </Field>
            <div className="modal-actions">
              <button type="button" className="btn" onClick={() => setCreating(false)}>
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={busy || !createName.trim() || !createOrgId}
              >
                {busy ? 'Creating…' : 'Create project'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {renaming && (
        <Modal title={`Rename ${renaming.name}`} onClose={() => setRenaming(null)}>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void rename();
            }}
          >
            <Field label="Project name">
              <input
                value={renameTo}
                onChange={(event) => setRenameTo(event.target.value)}
                required
                minLength={2}
                autoFocus
              />
            </Field>
            <div className="modal-actions">
              <button type="button" className="btn" onClick={() => setRenaming(null)}>
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={busy || !renameTo.trim()}
              >
                {busy ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
